import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import analyticsService from '../../services/analyticsService';
import './DashboardPage.css';

// Cache duration: 1 minute
const CACHE_TTL = 60 * 1000;
let dashboardCache = null;
let cacheTimestamp = null;

function DashboardPage() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboardData = useCallback(async (isManualRefresh = false) => {
    try {
      // Check cache if not a manual refresh
      if (!isManualRefresh && dashboardCache && cacheTimestamp) {
        const cacheAge = Date.now() - cacheTimestamp;
        if (cacheAge < CACHE_TTL) {
          // Use cached data
          setDashboardData(dashboardCache);
          setLastUpdated(new Date(cacheTimestamp));
          setLoading(false);
          return;
        }
      }

      if (!isManualRefresh) {
        setLoading(true);
      }
      setError(null);

      const response = await analyticsService.getDashboard();

      // Update cache
      dashboardCache = response.data;
      cacheTimestamp = Date.now();

      setDashboardData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');

      // Redirect to login if unauthorized
      if (err.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchDashboardData(true), 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  if (loading && !dashboardData) {
    return (
      <div className="dashboard-page-content">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="dashboard-page-content">
        <div className="dashboard-error">
          <p>{error}</p>
          <button onClick={() => fetchDashboardData()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page-content" role="main" aria-label="Dashboard">
      <div className="dashboard-welcome">
        <h1>Welcome back, {user?.name || 'Admin'}!</h1>
        <p>Here's what's happening with your store today.</p>
        {lastUpdated && (
          <p className="last-updated" aria-live="polite">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </div>

      <div className="dashboard-stats" role="region" aria-label="Key metrics">
        <div className="stat-card" role="article" aria-label="Total orders metric">
          <div className="stat-icon orders" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`${dashboardData?.totalOrders || 0} orders`}>
              {dashboardData?.totalOrders?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Total Orders</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon revenue">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              £{dashboardData?.totalRevenue?.toLocaleString('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) || '0.00'}
            </span>
            <span className="stat-label">Revenue</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon customers">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {dashboardData?.totalCustomers?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Customers</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon products">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value">
              {dashboardData?.totalProducts?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Products</span>
          </div>
        </div>
      </div>

      {/* Second row of stat cards */}
      <div className="dashboard-stats dashboard-stats-secondary" role="region" aria-label="Additional metrics">
        <div className="stat-card" role="article" aria-label="Pending orders metric">
          <div className="stat-icon pending" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`${dashboardData?.pendingOrders || 0} pending orders`}>
              {dashboardData?.pendingOrders?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Pending Orders</span>
          </div>
        </div>

        <div className="stat-card" role="article" aria-label="Completed orders metric">
          <div className="stat-icon completed" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`${dashboardData?.completedOrders || 0} completed orders`}>
              {dashboardData?.completedOrders?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Completed Orders</span>
          </div>
        </div>

        <div className="stat-card" role="article" aria-label="Failed orders metric">
          <div className="stat-icon failed" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`${dashboardData?.failedOrders || 0} failed orders`}>
              {dashboardData?.failedOrders?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Failed Orders</span>
          </div>
        </div>

        <div className={`stat-card ${(dashboardData?.lowStockProducts || 0) > 0 ? 'stat-card-warning' : ''}`} role="article" aria-label="Low stock alert">
          <div className="stat-icon warning" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`${dashboardData?.lowStockProducts || 0} products with low stock`}>
              {dashboardData?.lowStockProducts?.toLocaleString() || 0}
            </span>
            <span className="stat-label">Low Stock Alert</span>
          </div>
        </div>

        <div className="stat-card" role="article" aria-label="Average order value metric">
          <div className="stat-icon avg-order" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
            </svg>
          </div>
          <div className="stat-info">
            <span className="stat-value" aria-label={`Average order value £${dashboardData?.averageOrderValue || 0}`}>
              £{dashboardData?.averageOrderValue?.toLocaleString('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) || '0.00'}
            </span>
            <span className="stat-label">Avg Order Value</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card recent-orders">
          <div className="card-header">
            <h2>Recent Orders</h2>
            <a href="/orders" className="card-link">View all</a>
          </div>
          <div className="card-content">
            {dashboardData?.recentOrders && dashboardData.recentOrders.length > 0 ? (
              <div className="orders-table">
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Payment</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentOrders.map((order) => (
                      <tr key={order.orderId}>
                        <td className="order-id">{order.orderId}</td>
                        <td>{order.customerName}</td>
                        <td>{new Date(order.orderDate).toLocaleDateString()}</td>
                        <td>
                          <span className={`status-badge status-${order.paymentStatus?.toLowerCase() || 'pending'}`}>
                            {order.paymentStatus || 'Pending'}
                          </span>
                        </td>
                        <td className="order-total">
                          £{order.total.toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="placeholder-text">No recent orders found.</p>
            )}
          </div>
        </div>

        <div className="dashboard-card quick-actions">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="card-content">
            <div className="action-buttons">
              <a href="/products/new" className="action-button">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Product
              </a>
              <a href="/orders" className="action-button">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
                View Orders
              </a>
              <a href="/analytics" className="action-button">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
                Analytics
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
